import React from "react";
// Corrected: Use CDN imports to resolve module resolution errors in this environment
import { makeStyles } from "@material-ui/core/styles";
import { Paper, Typography, Grid, Box } from "@material-ui/core";
import { extractEstimator } from "./TotalPaid/PolicyMainCard";


// --- Icon Components (to keep everything in one file) ---

const PaidToPolicyIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
                  fillOpacity="0.3" />
            <path d="M12.5 11h-1V8c0-.55-.45-1-1-1s-1 .45-1 1v3h-1c-.55 0-1 .45-1 1s.45 1 1 1h1v1c0 .55 .45 1 1 1s1-.45 1-1v-1h1c.55 0 1-.45 1-1s-.45-1-1-1zm-1.5-6c.83 0 1.5.67 1.5 1.5S11.83 8 11 8s-1.5-.67-1.5-1.5S10.17 5 11 5zM17 17.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zM7 17.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            <path d="M13 10.06V8h-2v2.06c-1.14.5-2 1.66-2 2.94 0 1.82 1.48 3.3 3.3 3.3s3.3-1.48 3.3-3.3c0-1.28-.86-2.44-2-2.94zM12 15c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
        </svg>
);

const IndexGrowthIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z" />
        </svg>
);

const CashValueIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C9.23858 2 7 4.23858 7 7V8H4C3.44772 8 3 8.44772 3 9V17C3 19.7614 5.23858 22 8 22H16C18.7614 22 21 19.7614 21 17V9C21 8.44772 20.5523 8 20 8H17V7C17 4.23858 14.7614 2 12 2ZM11.5 12.5H13.5C14.0523 12.5 14.5 12.0523 14.5 11.5C14.5 10.9477 14.0523 10.5 13.5 10.5H12V9.5H14V8.5H12C11.1716 8.5 10.5 9.17157 10.5 10V10.5H9.5C8.94772 10.5 8.5 10.9477 8.5 11.5C8.5 12.0523 8.94772 12.5 9.5 12.5H11V13.5H9V14.5H11C11.8284 14.5 12.5 13.8284 12.5 13V12.5H11.5Z" fill="#00C5B4"/>
        </svg>
);

const LockIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z" />
        </svg>
);


// --- Type Definitions for TypeScript ---

interface CashValueChartProps {
    title: string;
    paidToPolicy: number;
    indexGrowthRate: number;
    cashValueTotal: number;
    chartData: number[];
    years: number[];
    maxYValue: number;
}

interface YAxisLabel {
    value: string;
    y: number;
}

// --- MUI v4 Styles using makeStyles ---
const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: "rgb(27, 38, 59)",
        color: "white",
        fontFamily: "sans-serif",
        padding: theme.spacing(3),
        borderRadius: "16px",
        boxShadow: theme.shadows[20],
        width: "100%",
        maxWidth: "896px",
        margin: "0 auto",
        border: "1px solid #00aaff",
        [theme.breakpoints.up("sm")]: {
            padding: theme.spacing(4),
        },
    },
    title: {
        color: "#e5e7eb",
        marginBottom: theme.spacing(4),
        fontWeight: 600,
    },
    summaryCardContainer: {
        marginBottom: theme.spacing(5),
    },
    summaryItem: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        textAlign: "center",
        flexDirection: "column",
    },
    summaryIcon: {
        width: "40px",
        height: "40px",
    },
    paidIcon: { color: "#facc15" },
    growthIcon: { color: "#ec4899" },
    cashValueIcon: { color: "#2dd4bf" },
    lockIcon: { color: "#60a5fa" },
}));


// --- Main Chart Component ---

const CashValueChart: React.FC<CashValueChartProps> = ({
                                                           title,
                                                           paidToPolicy,
                                                           indexGrowthRate,
                                                           cashValueTotal,
                                                           chartData,
                                                           years,
                                                           maxYValue,
                                                       }) => {
    const classes = useStyles();

    // SVG dimensions and padding
    const svgWidth = 800;
    const svgHeight = 500;
    const padding = { top: 20, right: 20, bottom: 60, left: 100 };
    const chartWidth = svgWidth - padding.left - padding.right;
    const chartHeight = svgHeight - padding.top - padding.bottom;

    // Calculate scales
    const xScale = chartWidth / (chartData.length > 1 ? chartData.length - 1 : 1);
    const yScale = chartHeight / maxYValue;

    // Create the line path data string
    const linePath = chartData
            .map((point, i) => {
                const x = padding.left + i * xScale;
                const y = svgHeight - padding.bottom - point * yScale;
                return `${x},${y}`;
            })
            .join(" ");

    // Create Y-axis labels using a more direct functional approach
    const yAxisInterval = 100000;
    const numberOfLabels = Math.floor(maxYValue / yAxisInterval);
    const yAxisLabels: YAxisLabel[] = Array.from({ length: numberOfLabels }, (_, index) => {
        const valueNum = (index + 1) * yAxisInterval;
        return {
            value: (valueNum / 1000).toLocaleString(),
            y: svgHeight - padding.bottom - valueNum * yScale,
        };
    });

    // Format top card values
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    return (
            <Paper className={classes.root} elevation={0}>
                <Typography variant="caption" className="main-title">
                    {title}
                </Typography>

                <Grid container spacing={4} className={classes.summaryCardContainer}>
                    <Grid item xs={12} sm={4}>
                        <div className={classes.summaryItem}>
                            <PaidToPolicyIcon className={`${classes.summaryIcon} ${classes.paidIcon}`} />
                            <Box>
                                <Typography variant="h5" component="p"
                                            style={{ fontWeight: "bold",marginBottom:0 }}>{formatCurrency(paidToPolicy)}</Typography>
                                <Typography variant="body2" style={{ color: "#9ca3af" }}>Paid to Policy</Typography>
                            </Box>
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <div className={classes.summaryItem}>
                            <IndexGrowthIcon className={`${classes.summaryIcon} ${classes.growthIcon}`} />
                            <Box>
                                <Typography variant="h5" component="p"
                                            style={{ fontWeight: "bold",marginBottom:0 }}>{indexGrowthRate}%</Typography>
                                <Typography variant="body2" style={{ color: "#9ca3af" }}>Index Growth Rate</Typography>
                            </Box>
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <div className={classes.summaryItem}>
                            <CashValueIcon className={`${classes.summaryIcon} ${classes.cashValueIcon}`} />
                            <Box>
                                <Typography variant="h5" component="p"
                                            style={{ fontWeight: "bold",marginBottom:0 }}>{formatCurrency(cashValueTotal)}</Typography>
                                <Typography variant="body2" style={{ color: "#9ca3af" }}>Cash Value Total</Typography>
                            </Box>
                        </div>
                    </Grid>
                </Grid>

                {/* SVG Chart */}
                <div style={{ width: "100%" }}>
                    <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width: "100%", height: "auto" }}>
                        {/* Y-axis line and labels */}
                        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={svgHeight - padding.bottom}
                              stroke="#374151" strokeWidth="1" />
                        {yAxisLabels.map(label => (
                                <text key={label.value} x={padding.left - 15} y={label.y} fill="#9ca3af"
                                      textAnchor="end" alignmentBaseline="middle" fontSize="16">
                                    {label.value}
                                </text>
                        ))}


                        {/* X-axis line and labels */}
                        <line x1={padding.left} y1={svgHeight - padding.bottom} x2={svgWidth - padding.right}
                              y2={svgHeight - padding.bottom} stroke="#374151" strokeWidth="1" />
                        {chartData.map((_, i) => (
                                <text key={`year-${i}`} x={padding.left + i * xScale}
                                      y={svgHeight - padding.bottom + 25} fill="#9ca3af" textAnchor="middle"
                                      fontSize="16">
                                    Year {years[i]}
                                </text>
                        ))}

                        {/* Main data line */}
                        <polyline
                                fill="none"
                                stroke="#2dd4bf"
                                strokeWidth="6"
                                strokeLinejoin="round"
                                strokeLinecap="round"
                                points={linePath}
                        />

                        {/* Legend Box */}
                        <g transform={`translate(${padding.left + 40}, ${padding.top + 5})`}>
                            <rect x="0" y="0" width="280" height="130" fill="#0b1a2a" stroke="#374151" rx="8" ry="8"
                                  fillOpacity="0.8" />

                            {/* Legend Items */}
                            <g transform="translate(20, 20)">
                                <line x1="0" y1="0" x2="30" y2="0" stroke="#2dd4bf" strokeWidth="3" />
                                <text x="40" y="5" fill="#e5e7eb" fontSize="15">Cash Value Accumulation</text>
                            </g>
                            <g transform="translate(20, 50)">
                                <line x1="0" y1="0" x2="30" y2="0" stroke="#f472b6" strokeWidth="3"
                                      strokeDasharray="3 5" />
                                <text x="40" y="5" fill="#e5e7eb" fontSize="15">Index Performance</text>
                            </g>
                            <g transform="translate(20, 80)">
                                <line x1="0" y1="0" x2="30" y2="0" stroke="#f59e0b" strokeWidth="3" />
                                <text x="40" y="5" fill="#e5e7eb" fontSize="15">0% Floor</text>
                            </g>
                            <g transform="translate(20, 110)">
                                <LockIcon className={classes.lockIcon} width="18" height="18" x="5" y="-9" />
                                <text x="40" y="5" fill="#e5e7eb" fontSize="15">Annual Reset</text>
                            </g>
                        </g>

                    </svg>
                </div>
            </Paper>
    );
};


const useAppStyles = makeStyles({
    app: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
});


type potentialCardProps = {
    estimationResponse: any;
};

export default function PotentialCardWrap(props: potentialCardProps) {
    const classes = useAppStyles();
    const { estimationResponse } = props;
    let totalPaid = 0
    let { common, contributionAmount,
        bankContribution, } = extractEstimator(estimationResponse);
    if((contributionAmount) || (bankContribution)){
        contributionAmount = contributionAmount * 5
        bankContribution = bankContribution * 5
        totalPaid = bankContribution + contributionAmount;
    }
    let growthRate:number = ((common && common.growthrate)*100) || 0;

    const reportData: CashValueChartProps = {
        title: "Your Potential Cash Value Growth Over 15 Years",
        paidToPolicy: contributionAmount,
        indexGrowthRate: growthRate,
        cashValueTotal: totalPaid + (totalPaid * (common && common.growthrate || 0)),

        // The actual values plotted on the chart
        chartData: [100000, 200000, 300000, 400000, 500000, 600000],

        // The labels for the x-axis
        years: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],

        // Set the max Y-axis value for scale. Should be higher than any data point.
        maxYValue: 600000,
    };

    return (
            <div className={classes.app}>
                <CashValueChart {...reportData} />
            </div>
    );
}

